import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Search, Pill, AlertCircle, Zap, AlertTriangle, Info, FileText, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API } from "@/config/constants";
import { useOfflineData } from "@/contexts/OfflineDataContext";

export default function MedicationsPage() {
  const { drugs: cachedDrugs, protocols, getDrugProtocols } = useOfflineData();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [relatedProtocols, setRelatedProtocols] = useState([]);
  const location = useLocation();
  const scrolledToHash = useRef(false);

  const drugs = [...cachedDrugs].sort((a, b) => a.name.localeCompare(b.name));

  // Reset hash tracking when hash changes
  useEffect(() => {
    scrolledToHash.current = false;
  }, [location.hash]);

  // Auto-scroll to drug from hash and open its detail
  useEffect(() => {
    const hash = location.hash?.replace("#", "");
    if (!hash || !drugs.length || scrolledToHash.current) return;
    const drug = drugs.find(d => d.id === hash);
    if (drug) {
      scrolledToHash.current = true;
      setSelectedDrug(drug);
      getDrugProtocols(drug.id).then(res => {
        setRelatedProtocols(res.protocols || []);
      }).catch(() => setRelatedProtocols([]));
      // Scroll after render
      setTimeout(() => {
        const el = document.getElementById(`drug-${hash}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  }, [location.hash, drugs, getDrugProtocols]);

  // Explicit indication-to-protocol-id mappings for common terms
  const INDICATION_MAPPINGS = {
    'metabolic acidosis': ['air-adult-dka'],
    'hyperkalemia': ['air-adult-hyperkalemia'],
    'crush entrapment': ['air-adult-crush-entrapment'],
    'tricyclic od': ['air-adult-tricyclic-od'],
    'tca overdose': ['air-adult-tricyclic-od'],
    'sympathomimetic od': ['air-adult-sympathomimetic-od'],
    'beta blocker od': ['air-adult-beta-blocker-od'],
    'methanol ingestion': ['air-adult-methanol'],
    'calcium channel blocker od': ['air-adult-calcium-channel-blocker-od'],
    'digoxin od': ['air-adult-digoxin-od'],
    'salicylate od': ['air-adult-salicylate-od'],
    'anticholinergic od': ['air-adult-anticholinergic'],
    'cardiac arrest': ['cardiac-arrest', 'air-adult-pulseless-arrest'],
    'seizures': ['seizures', 'air-adult-seizures'],
    'status epilepticus': ['seizures', 'air-adult-seizures'],
    'anaphylaxis': ['anaphylaxis', 'air-adult-anaphylactic-shock'],
    'anaphylactic shock': ['anaphylactic-shock', 'air-adult-anaphylactic-shock'],
    'bronchoconstriction': ['bronchoconstriction', 'air-adult-bronchoconstriction'],
    'pain control': ['pain-control', 'air-adult-pain-control'],
    'hypoglycemia': ['hypoglycemia'],
    'hypothermia': ['hypothermia', 'air-adult-hypothermia'],
    'crush syndrome': ['air-adult-crush-entrapment'],
  };

  const findProtocolForIndication = (indication) => {
    if (!protocols.length) return null;
    const indicationLower = indication.toLowerCase();
    
    // Check explicit mappings first
    const mappedIds = INDICATION_MAPPINGS[indicationLower];
    if (mappedIds) {
      for (const pid of mappedIds) {
        const found = protocols.find(p => p.id === pid);
        if (found) return found;
      }
    }
    
    // Exact name match
    let match = protocols.find(p => p.name.toLowerCase() === indicationLower);
    if (match) return match;
    // Partial name match
    match = protocols.find(p => {
      const protocolLower = p.name.toLowerCase();
      return protocolLower.includes(indicationLower) || indicationLower.includes(protocolLower);
    });
    if (match) return match;
    // Keyword match
    match = protocols.find(p => 
      p.keywords?.some(k => k.toLowerCase() === indicationLower || indicationLower.includes(k.toLowerCase()))
    );
    return match;
  };

  const IndicationLink = ({ indication }) => {
    const protocol = findProtocolForIndication(indication);
    if (protocol) {
      return (
        <Link 
          to={`/protocol/${protocol.id}`}
          className="text-primary hover:underline"
          data-testid={`indication-link-${protocol.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {indication}
        </Link>
      );
    }
    return <span>{indication}</span>;
  };

  const filteredDrugs = drugs.filter(drug => {
    const matchesSearch = !searchQuery || 
      drug.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      drug.indications.some(ind => ind.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesSearch;
  });

  const groupedDrugs = filteredDrugs.reduce((acc, drug) => {
    const letter = drug.name[0].toUpperCase();
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(drug);
    return acc;
  }, {});

  const availableLetters = Object.keys(groupedDrugs).sort();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Pill className="w-6 h-6" />
          Medications
        </h1>
        <p className="text-muted-foreground">{drugs.length} medications available (A-Z)</p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search medications..."
          className="pl-10"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          data-testid="medication-search"
        />
      </div>

      <div className="flex flex-wrap gap-1">
        {availableLetters.map(letter => (
          <a
            key={letter}
            href={`#letter-${letter}`}
            className="w-8 h-8 flex items-center justify-center rounded bg-secondary/50 hover:bg-primary hover:text-primary-foreground transition-colors text-sm font-medium"
            data-testid={`letter-tab-${letter}`}
          >
            {letter}
          </a>
        ))}
      </div>

      <div className="space-y-8">
        {availableLetters.map(letter => (
          <div key={letter} id={`letter-${letter}`} className="scroll-mt-20">
            <h2 className="font-heading text-xl font-bold mb-4 border-b border-border pb-2 sticky top-14 bg-background z-10">
              {letter}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {groupedDrugs[letter].map(drug => (
                <Card 
                  key={drug.id}
                  id={`drug-${drug.id}`}
                  className={`cursor-pointer hover:border-primary/50 transition-colors ${selectedDrug?.id === drug.id ? 'border-primary ring-2 ring-primary/20' : ''}`}
                  onClick={() => {
                    if (selectedDrug?.id === drug.id) {
                      setSelectedDrug(null);
                      setRelatedProtocols([]);
                    } else {
                      setSelectedDrug(drug);
                      getDrugProtocols(drug.id).then(res => {
                        setRelatedProtocols(res.protocols || []);
                      }).catch(() => setRelatedProtocols([]));
                    }
                  }}
                  data-testid={`medication-card-${drug.id}`}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-heading">{drug.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {drug.route.map(r => (
                        <Badge key={r} variant="secondary" className="text-xs">{r}</Badge>
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {drug.indications.slice(0, 2).join(", ")}
                      {drug.indications.length > 2 && "..."}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      {filteredDrugs.length === 0 && (
        <Card className="p-8 text-center">
          <Pill className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No medications found matching your search.</p>
        </Card>
      )}

      {selectedDrug && (
        <Card className="border-primary mt-4 fixed bottom-0 left-0 right-0 sm:bottom-4 sm:left-auto sm:right-4 sm:w-[500px] max-h-[60vh] sm:max-h-[70vh] overflow-auto z-50 shadow-xl sm:rounded-lg rounded-t-lg rounded-b-none">
          <CardHeader className="sticky top-0 bg-card z-10 border-b">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="font-heading text-xl">{selectedDrug.name}</CardTitle>
                <Badge variant="outline" className="mt-1">{selectedDrug.classification || selectedDrug.class_name}</Badge>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSelectedDrug(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            {selectedDrug.mechanism_of_action && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <span className="text-sm font-medium flex items-center gap-2 text-blue-500">
                  <Zap className="w-4 h-4" />
                  Mechanism of Action
                </span>
                <p className="text-sm mt-1 whitespace-pre-line">{selectedDrug.mechanism_of_action}</p>
              </div>
            )}
            
            {selectedDrug.route && selectedDrug.route.length > 0 && (
              <div>
                <span className="text-sm font-medium text-muted-foreground">Routes</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedDrug.route.map(r => (
                    <Badge key={r} variant="secondary">{r}</Badge>
                  ))}
                </div>
              </div>
            )}
            
            {selectedDrug.indications && selectedDrug.indications.length > 0 && (
              <div>
                <span className="text-sm font-medium text-muted-foreground">Indications (Links to Protocols)</span>
                <ul className="list-disc list-inside text-sm mt-1">
                  {selectedDrug.indications.map((ind, i) => (
                    <li key={i}><IndicationLink indication={ind} /></li>
                  ))}
                </ul>
              </div>
            )}
            
            {selectedDrug.contraindications && selectedDrug.contraindications.length > 0 && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <span className="text-sm font-medium flex items-center gap-2 text-red-500">
                  <AlertCircle className="w-4 h-4" />
                  Contraindications
                </span>
                <ul className="list-disc list-inside text-sm mt-1">
                  {selectedDrug.contraindications.map((con, i) => (
                    <li key={i}>{con}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {selectedDrug.cautions && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <span className="text-sm font-medium flex items-center gap-2 text-yellow-500">
                  <AlertTriangle className="w-4 h-4" />
                  Cautions
                </span>
                <p className="text-sm mt-1 whitespace-pre-line">{selectedDrug.cautions}</p>
              </div>
            )}
            
            {selectedDrug.adverse_reactions && (
              <div>
                <span className="text-sm font-medium text-muted-foreground">Adverse Reactions</span>
                <p className="text-sm mt-1 whitespace-pre-line">{selectedDrug.adverse_reactions}</p>
              </div>
            )}
            
            {selectedDrug.preparations && (
              <div className="p-3 bg-secondary/20 rounded-lg">
                <span className="text-sm font-medium text-muted-foreground">Preparations</span>
                <p className="text-sm mt-1 font-mono whitespace-pre-line">{selectedDrug.preparations}</p>
              </div>
            )}
            
            {selectedDrug.notes && selectedDrug.notes.length > 0 && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <span className="text-sm font-medium flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-500" />
                  Notes
                </span>
                <ul className="list-disc list-inside text-sm mt-1">
                  {selectedDrug.notes.map((note, i) => (
                    <li key={i}>{note}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {selectedDrug.pharmacokinetics && (
              <div className="p-3 bg-secondary/20 rounded-lg">
                <span className="text-sm font-medium text-muted-foreground">Pharmacokinetics</span>
                <p className="text-sm mt-1 font-mono whitespace-pre-line">{selectedDrug.pharmacokinetics}</p>
              </div>
            )}

            {relatedProtocols.length > 0 && (
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg" data-testid="drug-related-protocols">
                <span className="text-sm font-medium flex items-center gap-2 text-green-500">
                  <FileText className="w-4 h-4" />
                  Referenced in {relatedProtocols.length} Protocol{relatedProtocols.length !== 1 ? 's' : ''}
                </span>
                <ul className="mt-2 space-y-1">
                  {relatedProtocols.map((p) => (
                    <li key={p.id}>
                      <Link
                        to={`/protocol/${p.id}`}
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                        data-testid={`drug-protocol-link-${p.id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ChevronRight className="w-3 h-3" />
                        {p.name}
                        {p.service === "wes" ? (
                          <Badge className="ml-1 text-[10px] px-1.5 py-0 bg-orange-500/15 text-orange-500 border-orange-500/30" variant="outline">PCP-PS</Badge>
                        ) : (
                          <Badge variant="outline" className="ml-1 text-xs">{p.category}</Badge>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
